// Operational graph maths for the link map: roles, path to core, failure
// impact, fit. Pure so `bun run src/lib/netgraph.check.ts` can check it.
import { NODE_H, NODE_W, type Point } from "./netlayout";
import type { Box } from "./viewbox";
import type { NetMapEdge, NetMapNode } from "./types";

export type Role = "core" | "agg" | "router" | "switch" | "olt" | "device" | "foreign";
export type NodeState = "up" | "warn" | "down" | "unknown";
export type LinkState = "down" | "high" | "moderate" | "healthy" | "nodata";

/** Edge ids are only unique WITHIN an origin (net_links vs librenms_links). */
export const edgeKey = (e: Pick<NetMapEdge, "origin" | "id">) => `${e.origin}:${e.id}`;

export function linkState(e: Pick<NetMapEdge, "state" | "util_pct">): LinkState {
  if (e.state === "down") return "down";
  if (e.util_pct === null || Number.isNaN(e.util_pct)) return "nodata";
  if (e.util_pct > 80) return "high";
  return e.util_pct >= 50 ? "moderate" : "healthy";
}

// ponytail: warn is fixed at cpu >= 85% / mem >= 90%; the payload carries no per-device limits.
export function nodeState(n: Pick<NetMapNode, "kind" | "status" | "cpu_pct" | "mem_pct">): NodeState {
  if (n.kind === "neighbour" || n.status === "unknown") return "unknown";
  if (n.status === "down") return "down";
  return (n.cpu_pct ?? 0) >= 85 || (n.mem_pct ?? 0) >= 90 ? "warn" : "up";
}

/** `edge` names the pair (the manual edge when there is one); `keys` is every edge between the two. */
type Adj = Map<number, { peer: number; edge: string; keys: string[] }[]>;

/**
 * One adjacency per device PAIR: LibreNMS rediscovers an operator-drawn link
 * over LLDP, and counting that duplicate as a second path would make every
 * single-homed OLT look redundant. Failing any of a pair's edges fails the pair.
 */
export function adjacency(nodes: Pick<NetMapNode, "id">[], edges: NetMapEdge[]): Adj {
  const adj: Adj = new Map(nodes.map((n) => [n.id, []]));
  const pairs = new Map<string, { edge: string; keys: string[] }>();
  for (const e of [...edges].sort((a, b) => (a.origin === b.origin ? 0 : a.origin === "manual" ? -1 : 1))) {
    if (e.source === e.target || !adj.has(e.source) || !adj.has(e.target)) continue;
    const pk = e.source < e.target ? `${e.source}-${e.target}` : `${e.target}-${e.source}`;
    const known = pairs.get(pk);
    if (known) { known.keys.push(edgeKey(e)); continue; }
    const link = { edge: edgeKey(e), keys: [edgeKey(e)] };
    pairs.set(pk, link);
    adj.get(e.source)!.push({ peer: e.target, ...link });
    adj.get(e.target)!.push({ peer: e.source, ...link });
  }
  return adj;
}

const ROUTER_RE = /routeros|junos|ios|vrp|vyos|ccr|\bmx\d|router|asr|ne\d/i;
const SWITCH_RE = /crs|css|switch|swos|\bex\d|\bs\d{4}|edgeswitch|procurve/i;

/**
 * The payload has no device role, so it is inferred and labelled as such.
 * "Infrastructure degree" counts only neighbours that are not leaves: an
 * access switch with 40 CPEs is busy, not central. Core = the monitored
 * device(s) with the most infrastructure neighbours (total links break ties;
 * remaining ties are all core: redundant cores); aggregation = 2+; then
 * switch/router from the OS and hardware strings. `pinned` (operator-marked
 * cores) overrides the core guess entirely.
 * ponytail: `pinned` lives in the browser; a stored per-node role (next to net positions) is the upgrade.
 */
export function inferRoles(nodes: NetMapNode[], adj: Adj, pinned: Set<number> = new Set()): Map<number, Role> {
  // Foreign neighbours do not count: toggling that layer must not re-role devices.
  const foreign = new Set(nodes.filter((n) => n.kind === "neighbour").map((n) => n.id));
  const peers = (id: number) => [...new Set(adj.get(id)?.map((l) => l.peer).filter((q) => !foreign.has(q)))];
  const deg = (id: number) => peers(id).length;
  const infra = (id: number) => peers(id).filter((q) => deg(q) >= 2).length;
  const monitored = nodes.filter((n) => n.kind === "device");
  const score = (id: number) => infra(id) * 10_000 + deg(id);
  const top = Math.max(0, ...monitored.filter((n) => deg(n.id) >= 2).map((n) => score(n.id)));
  const cores = [...pinned].some((id) => adj.has(id))
    ? pinned
    : new Set(monitored.filter((n) => top > 0 && deg(n.id) >= 2 && score(n.id) === top).map((n) => n.id));
  const out = new Map<number, Role>();
  for (const n of nodes) {
    const hw = `${n.hardware ?? ""} ${n.os ?? ""}`;
    out.set(
      n.id,
      n.kind === "neighbour" ? "foreign"
        : cores.has(n.id) ? "core"
        : n.kind === "olt" ? "olt"
        : infra(n.id) >= 2 ? "agg"
        : SWITCH_RE.test(hw) ? "switch"
        : ROUTER_RE.test(hw) ? "router"
        : "device",
    );
  }
  return out;
}

/** Shortest path (edge keys) from `from` to the nearest node in `goals`, avoiding `banned`. */
function bfsPath(from: number, goals: Set<number>, adj: Adj, banned: Set<string>): string[] | null {
  const prev = new Map<number, { node: number; edge: string } | null>([[from, null]]);
  for (let q = [from]; q.length; ) {
    const next: number[] = [];
    for (const id of q) {
      if (goals.has(id)) {
        const path: string[] = [];
        for (let p = prev.get(id); p; p = prev.get(p.node)) path.push(p.edge);
        return path.reverse();
      }
      for (const l of adj.get(id) ?? []) {
        if (l.keys.some((k) => banned.has(k)) || prev.has(l.peer)) continue;
        prev.set(l.peer, { node: id, edge: l.edge });
        next.push(l.peer);
      }
    }
    q = next;
  }
  return null;
}

export type CorePath = { primary: string[]; backup: string[] | null; nodes: Set<number> };

/**
 * The route back to core: the shortest path, plus the shortest one that shares
 * no link with it (the redundant leg), when one exists.
 */
export function pathToCore(from: number, cores: Set<number>, adj: Adj, ends: Map<string, [number, number]>): CorePath | null {
  const primary = bfsPath(from, cores, adj, new Set());
  if (!primary) return null;
  const backup = primary.length ? bfsPath(from, cores, adj, new Set(primary)) : null;
  const nodes = new Set([from]);
  for (const k of [...primary, ...(backup ?? [])]) ends.get(k)?.forEach((id) => nodes.add(id));
  return { primary, backup, nodes };
}

/**
 * Nodes that lose every path to core when `failed` (a node id or an edge key)
 * is removed: the topology's inference of impact, not an observation.
 */
export function impactOf(failed: { node?: number; edge?: string }, cores: Set<number>, adj: Adj): Set<number> {
  const reach = (skipNode?: number, skipEdge?: string) => {
    const seen = new Set<number>();
    const q = [...cores].filter((c) => c !== skipNode);
    q.forEach((c) => seen.add(c));
    while (q.length) {
      const id = q.pop()!;
      for (const l of adj.get(id) ?? []) {
        if ((skipEdge && l.keys.includes(skipEdge)) || l.peer === skipNode || seen.has(l.peer)) continue;
        seen.add(l.peer);
        q.push(l.peer);
      }
    }
    return seen;
  };
  const before = reach();
  const after = reach(failed.node, failed.edge);
  const out = new Set<number>();
  for (const id of before) if (!after.has(id) && id !== failed.node) out.add(id);
  return out;
}

/**
 * View box that shows every point with the graph filling `fill` of the
 * limiting axis ("meet" scaling letterboxes the other one). A floor stops a
 * two-node map from zooming into giant boxes.
 */
export function fitBox(points: Point[], fill = 0.8): Box {
  if (points.length === 0) return { x: -450, y: -250, w: 900, h: 500 };
  const xs = points.map((p) => p.x), ys = points.map((p) => p.y);
  const minX = Math.min(...xs) - NODE_W / 2, maxX = Math.max(...xs) + NODE_W / 2;
  const minY = Math.min(...ys) - NODE_H / 2, maxY = Math.max(...ys) + NODE_H / 2;
  const w = Math.max((maxX - minX) / fill, 900), h = Math.max((maxY - minY) / fill, 500);
  return { x: (minX + maxX) / 2 - w / 2, y: (minY + maxY) / 2 - h / 2, w, h };
}

/** A box centred on one point at a close, readable zoom. */
export const focusBox = (p: Point, w = 1100, h = 620): Box => ({ x: p.x - w / 2, y: p.y - h / 2, w, h });

/**
 * Names are SNMP sysNames or LLDP free text, so two devices often share one
 * ("olt", "mikrotik"). Colliding names get the management address appended;
 * unique names are left alone.
 */
export function disambiguate<N extends Pick<NetMapNode, "name" | "hostname">>(nodes: N[]): N[] {
  const seen = new Map<string, number>();
  for (const n of nodes) seen.set(n.name.trim().toLowerCase(), (seen.get(n.name.trim().toLowerCase()) ?? 0) + 1);
  return nodes.map((n) =>
    (seen.get(n.name.trim().toLowerCase()) ?? 0) > 1 && n.hostname && n.hostname !== n.name
      ? { ...n, name: `${n.name} · ${n.hostname}` }
      : n);
}

/** Middle ellipsis: "kedungmalang-olt-2" -> "kedungm…olt-2". Sibling names usually differ at the end. */
export function midTrim(s: string, max: number): string {
  if (s.length <= max) return s;
  const head = Math.ceil((max - 1) * 0.55);
  return `${s.slice(0, head)}…${s.slice(s.length - (max - 1 - head))}`;
}
